import horoscope from './assets/horoscope.svg';
import star from './assets/star.svg';
import zodiac from './assets/zodiac-wheel.svg';
import blackTrade from './assets/black-trade.svg';
import demoTrade from './assets/demo-trade.svg';
import tracking from './assets/trade-tracking.svg';
import artHoro from './assets/art/art-horo.svg';
import artTrade from './assets/art/art-trade.svg';
import artMind from './assets/art/art-mind.svg';
import artRisk from './assets/art/art-risk.svg';
import ownerHero from './assets/owner-hero.webp';
import ownerSmile from './assets/owner-smile.webp';

/**
 * Owner portraits — studio photos supplied by the owner (5 Nov 2026), background removed with
 * scripts/cutout.html (edge-flood keying + colour decontamination, so no grey halo on navy).
 *  - owner-hero.webp 894×1239: seated, hands clasped, looking toward the headline (hero).
 *  - owner-smile.webp 424×656: arms crossed, smiling (live card).
 * Set to null to fall back to the labeled placeholder slot.
 */
export const OWNER_PORTRAIT: string | null = ownerHero;
export const OWNER_PORTRAIT_SIZE = { w: 894, h: 1239 };
export const OWNER_PORTRAIT_SM: string | null = ownerSmile;
export const OWNER_PORTRAIT_SM_SIZE = { w: 424, h: 656 };
export const OWNER_ALT = 'เจ้าของโหราเทรดเดอร์ ในชุดสูทสีน้ำเงิน';

/*
 * Goals of Horatrader.com (from owner, 5 Nov 2026), in priority order:
 *  1. CRM — เก็บ ติดตาม และเพิ่มสมาชิก
 *  2. ความน่าเชื่อถือของแบรนด์
 *  3. ขายคอร์สสอนเทรด (ไม่แนะนำโบรกเกอร์) — ไม่มีคอร์สสอนดูดวง
 *  4. เช็กดวงการเทรดตามวัน เดือน ปี (ดาวเข้า / ดาวย้ายออก / เหมาะหรือไม่เหมาะแก่การเทรด) — เฉพาะสมาชิก
 * Rules: ไม่ชักชวนลงทุน · ไม่แนะนำโบรกเกอร์ · โทน สวย หรู พรีเมียม ลึกลับ น่าเชื่อถือ
 */

/** Every menu item is its own page (owner, 5 Nov 2026). */
export const NAV = [
  { to: '/', label: 'หน้าหลัก' },
  { to: '/courses', label: 'คอร์สเทรด' },
  { to: '/horoscope', label: 'เช็กดวงเทรด' },
  { to: '/black-trade', label: 'Black Trade' },
  { to: '/demo-trade', label: 'Demo Trade' },
  { to: '/clips', label: 'คลังวิดีโอ' },
  { to: '/articles', label: 'บทความ' },
  { to: '/news', label: 'ข่าวสาร' },
] as const;

export interface PillarItem { key: string; icon: string; title: string; desc: string; target?: string }
export interface Pillar { id: string; eyebrow: string; title: string; lead: string; img: string; items: PillarItem[] }

export const PILLARS: Pillar[] = [
  {
    id: 'trader',
    eyebrow: 'TRADER',
    title: 'มุมเทรดเดอร์',
    lead: 'ฝึกอ่านกราฟอย่างมีวินัย ทดลองกับข้อมูลจำลอง และทบทวนผลของตัวเอง',
    img: artTrade,
    items: [
      { key: 'p-black', icon: blackTrade, title: 'Black Trade', desc: 'ย้อนกราฟในอดีตและจำลองการตัดสินใจ', target: '/black-trade' },
      { key: 'p-demo', icon: demoTrade, title: 'Demo Trade', desc: 'ฝึกด้วยพอร์ตจำลอง ไม่ใช้เงินจริง', target: '/demo-trade' },
      { key: 'p-track', icon: tracking, title: 'ติดตามผลการเทรด', desc: 'บันทึกและทบทวนพัฒนาการของคุณ' },
    ],
  },
  {
    id: 'astro',
    eyebrow: 'MEMBERS ONLY',
    title: 'ดวงการเทรด',
    lead: 'เครื่องมือเช็กจังหวะดวงดาวสำหรับสมาชิก ดูดาวเข้า ดาวย้ายออก ตามวัน เดือน ปี',
    img: artHoro,
    items: [
      { key: 'p-horo', icon: horoscope, title: 'ดวงการเทรดวันนี้', desc: 'เปิดดูจังหวะดวงดาวประจำวัน', target: '/horoscope' },
      { key: 'p-date', icon: star, title: 'เช็กตามวัน เดือน ปี', desc: 'เลือกวันที่ที่ต้องการดูล่วงหน้าหรือย้อนหลัง', target: '/horoscope' },
      { key: 'p-transit', icon: zodiac, title: 'ดาวเข้า · ดาวย้ายออก', desc: 'สรุปว่าวันนั้นเหมาะหรือไม่เหมาะแก่การเทรด', target: '/horoscope' },
    ],
  },
];

/** Trading courses only — no astrology courses are sold. Titles/prices wait for the owner. */
export const COURSE_LEVELS = [
  { num: 'I', key: 'c-basic', level: 'พื้นฐาน', desc: 'อ่านกราฟ แท่งเทียน และโครงสร้างตลาด' },
  { num: 'II', key: 'c-mid', level: 'ขั้นกลาง', desc: 'วางแผนการเทรดและบริหารความเสี่ยง' },
  { num: 'III', key: 'c-adv', level: 'ขั้นสูง', desc: 'สร้างระบบ วินัย และการทบทวนผลอย่างต่อเนื่อง' },
] as const;

export const JOURNEY = [
  { key: 'j1', icon: 'user', title: 'สมัครสมาชิก', desc: 'เปิดพื้นที่ส่วนตัวของคุณ' },
  { key: 'j2', icon: 'moon', title: 'เช็กดวงการเทรด', desc: 'ดูดาวเข้า ดาวย้ายออก ตามวันที่' },
  { key: 'j3', icon: 'book', title: 'เรียนคอร์สเทรด', desc: 'จากพื้นฐานสู่ขั้นสูง' },
  { key: 'j4', icon: 'chart', title: 'ติดตามพัฒนาการ', desc: 'บันทึก ทบทวน และเห็นความก้าวหน้า' },
] as const;

export const ARTICLE_TOPICS = [
  { key: 'a-astro', img: artHoro, title: 'ดวงการเทรด', desc: 'ดาวเข้า ดาวย้าย และจังหวะเวลา' },
  { key: 'a-mind', img: artMind, title: 'จิตวิทยาการเทรด', desc: 'อารมณ์ วินัย และการตัดสินใจ' },
  { key: 'a-risk', img: artRisk, title: 'การบริหารความเสี่ยง', desc: 'ปกป้องตัวเองก่อนคิดถึงผลลัพธ์' },
];

/** CRM sign-up form — interests a member can tick. */
export const INTERESTS = [
  { key: 'course', label: 'คอร์สสอนเทรด' },
  { key: 'horo', label: 'เช็กดวงการเทรด' },
  { key: 'practice', label: 'Black Trade / Demo Trade' },
] as const;
